import React, { useCallback, useEffect, useState } from "react";
import { getAuthToken } from "../../utils/authHelpers";

/**
 * Admin — Collab Reconciliation (PRD 6)
 *
 * Finds collaborations that never reached 'completed' despite clear evidence
 * (one-sided confirmations, mutual accepted reviews, orphaned accepted
 * requests) and lets an admin backfill or dismiss each candidate.
 *
 * Internal tool: function over polish. Auth = admin role on the JWT (same
 * gate as the admin override-complete endpoint).
 */

const KIND_LABELS = {
  one_sided: 'One-sided completion',
  review_pair: 'Mutual reviews, no collab',
  accepted_request: 'Accepted, never tracked',
};

const CollabReconciliation = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [resolvedKeys, setResolvedKeys] = useState(new Set());

  const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4000'
    : '';

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    const token = getAuthToken();
    if (!token) {
      setError('Please log in as an admin.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${apiBase}/api/admin/collab-reconciliation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error === 'forbidden' ? 'Admin access required.' : (data.error || 'Failed to load'));
        return;
      }
      setCandidates(data.candidates || []);
      setResolvedKeys(new Set());
    } catch (err) {
      console.error('Reconciliation fetch failed:', err);
      setError('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const act = async (candidate, action) => {
    if (busyKey) return;
    if (action === 'backfill' && !window.confirm(
      `Create and mark completed: ${candidate.label}?\n\nEvidence: ${candidate.evidence}`
    )) return;

    setBusyKey(candidate.candidateKey);
    const token = getAuthToken();
    try {
      const res = await fetch(`${apiBase}/api/admin/collab-reconciliation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          action === 'backfill'
            ? { action, candidate }
            : { action, candidateKey: candidate.candidateKey }
        ),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        alert('Failed: ' + (data.error || 'Unknown error'));
        return;
      }
      setResolvedKeys(prev => new Set([...prev, candidate.candidateKey]));
    } catch (err) {
      console.error('Reconciliation action failed:', err);
      alert('Action failed');
    } finally {
      setBusyKey(null);
    }
  };

  const visible = candidates.filter(c => !resolvedKeys.has(c.candidateKey));

  const cellStyle = { padding: '10px 12px', fontSize: 14, borderBottom: '1px solid #eee', verticalAlign: 'top' };
  const btnStyle = (primary) => ({
    padding: '6px 14px', borderRadius: 100, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: `1.5px solid ${primary ? '#0f084b' : '#d1d5db'}`,
    background: primary ? '#0f084b' : '#ffffff',
    color: primary ? '#ffffff' : '#6b7280',
    marginRight: 8,
  });

  return (
    <div style={{ maxWidth: 1080, margin: '40px auto', padding: '0 20px', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif" }}>
      <h1 style={{ fontFamily: "'Sailors', serif", color: '#0f084b', fontSize: 30, marginBottom: 6 }}>
        Collab Reconciliation
      </h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24, maxWidth: 720 }}>
        Collabs that likely completed but never got counted. Backfilled collabs are marked
        completed with source <code>admin_backfill</code> and count toward public profile numbers.
      </p>

      <button onClick={fetchCandidates} disabled={loading} style={btnStyle(false)}>
        {loading ? 'Loading…' : 'Refresh'}
      </button>

      {error && (
        <div style={{ margin: '16px 0', padding: '12px 16px', background: 'rgba(220,29,8,0.08)', color: '#b51706', borderRadius: 10, fontSize: 14 }}>
          {error}
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div style={{ margin: '24px 0', padding: '24px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: 12 }}>
          No reconciliation candidates — counts look clean.
        </div>
      )}

      {visible.length > 0 && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginTop: 18 }}>
        <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
              <th style={{ ...cellStyle, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Type</th>
              <th style={{ ...cellStyle, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Who</th>
              <th style={{ ...cellStyle, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Evidence</th>
              <th style={{ ...cellStyle, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Inferred date</th>
              <th style={{ ...cellStyle, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(candidate => (
              <tr key={candidate.candidateKey}>
                <td style={cellStyle}>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 100, fontSize: 12,
                    fontWeight: 600, background: '#f4f3fb', color: '#0f084b', whiteSpace: 'nowrap',
                  }}>
                    {KIND_LABELS[candidate.kind] || candidate.kind}
                  </span>
                </td>
                <td style={{ ...cellStyle, fontWeight: 600, color: '#0f084b' }}>{candidate.label}</td>
                <td style={{ ...cellStyle, color: '#374151' }}>{candidate.evidence}</td>
                <td style={{ ...cellStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {candidate.inferredDate ? new Date(candidate.inferredDate).toLocaleDateString() : '—'}
                </td>
                <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                  <button
                    style={btnStyle(true)}
                    disabled={busyKey === candidate.candidateKey}
                    onClick={() => act(candidate, 'backfill')}
                  >
                    {busyKey === candidate.candidateKey ? 'Working…' : (candidate.kind === 'one_sided' ? 'Mark completed' : 'Create + complete')}
                  </button>
                  <button
                    style={btnStyle(false)}
                    disabled={busyKey === candidate.candidateKey}
                    onClick={() => act(candidate, 'dismiss')}
                  >
                    Dismiss
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default CollabReconciliation;
